/** Self-refreshing task log viewer.
 *
 * Live tasks: entries are cached locally and every poll fetches ONLY the diff
 * (`?after=<last stream id>`), appending to the cache. Terminal tasks: one
 * fetch (live remainder or S3 archive). Auto-refresh is toggleable (defaults
 * to on for active tasks, off for finished ones) and follows the tail while
 * on. Search filters the local cache and highlights matches; levels are
 * color-coded; URLs render as links; the full cache downloads as JSON.
 */
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, type BackgroundTaskDetail, type TaskLogEntry } from '@/api/bgtasks';

import { levelColor, LinkifiedText } from './taskFormat';

const LOG_POLL_MS = 3000;

/** Case-insensitive substring highlighter (used while a search is active —
 *  takes over from LinkifiedText for the matched view). */
function HighlightedText({ text, query }: { text: string; query: string }) {
  const q = query.toLowerCase();
  if (!q) return <>{text}</>;
  const parts: React.ReactNode[] = [];
  let rest = text;
  let idx = rest.toLowerCase().indexOf(q);
  let key = 0;
  while (idx >= 0) {
    if (idx > 0) parts.push(<span key={key++}>{rest.slice(0, idx)}</span>);
    parts.push(
      <Box
        key={key++}
        component="mark"
        sx={{ bgcolor: 'warning.light', color: 'inherit', px: 0.1, borderRadius: 0.5 }}
      >
        {rest.slice(idx, idx + q.length)}
      </Box>,
    );
    rest = rest.slice(idx + q.length);
    idx = rest.toLowerCase().indexOf(q);
  }
  if (rest) parts.push(<span key={key++}>{rest}</span>);
  return <>{parts}</>;
}

interface Props {
  task: BackgroundTaskDetail;
  /** Writes a line's timestamp (e.g. a wall clock in the tenant's zone). Default: as sent. */
  formatTs?: (ts: string) => string;
}

export function TaskLogsTab({ task, formatTs }: Props) {
  const [entries, setEntries] = useState<TaskLogEntry[]>([]);
  const [source, setSource] = useState<'live' | 'archive' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState('');
  // Default: follow a running task, leave finished ones static.
  const [autoRefresh, setAutoRefresh] = useState(() => isActiveState(task.state));
  const lastIdRef = useRef<string | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const active = isActiveState(task.state);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const poll = async () => {
      try {
        const r = await bgtasksApi.logs(task.id, lastIdRef.current ?? undefined);
        if (cancelled) return;
        setError(null);
        setLoaded(true);
        setSource(r.source);
        if (r.source === 'archive') {
          // Archive is the complete log — replace, never append.
          setEntries(r.entries);
        } else if (r.entries.length) {
          lastIdRef.current = r.last_id;
          setEntries((prev) => [...prev, ...r.entries]);
        } else if (r.last_id && !lastIdRef.current) {
          lastIdRef.current = r.last_id;
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
      if (!cancelled && autoRefresh) {
        timer = window.setTimeout(() => void poll(), LOG_POLL_MS);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // Re-arm on toggle changes and when the task transitions active →
    // terminal (one more fetch picks up the remainder / the archive).
  }, [task.id, active, autoRefresh]);

  // Tail-style follow: while auto-refresh is on, pin to the bottom on open
  // and after every appended batch.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || !autoRefresh) return;
    el.scrollTop = el.scrollHeight;
  }, [entries.length, autoRefresh, loaded]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        (e.msg ?? '').toLowerCase().includes(q) ||
        (e.levelname ?? '').toLowerCase().includes(q),
    );
  }, [entries, search]);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(entries, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bgtask-${task.id}-logs.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const query = search.trim();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search logs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Auto-refresh{autoRefresh ? ` (${LOG_POLL_MS / 1000}s)` : ''}
            </Typography>
          }
        />
        <Tooltip title="Download logs as JSON">
          <span>
            <IconButton size="small" onClick={downloadJson} disabled={!entries.length}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {source && <Chip size="small" variant="outlined" label={source} />}
        <Typography variant="caption" color="text.secondary">
          {filtered.length}/{entries.length} lines
        </Typography>
      </Box>

      {error && <Alert severity="warning">Log fetch failed: {error}</Alert>}

      <Box
        ref={boxRef}
        sx={{
          flex: 1,
          minHeight: 380,
          maxHeight: '62vh',
          overflow: 'auto',
          bgcolor: 'grey.50',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          fontFamily: 'monospace',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {!loaded && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {loaded && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
            No log entries{active ? ' yet' : ''}.
          </Typography>
        )}
        {filtered.map((e, i) => (
          <Box key={e.id ?? i} sx={{ color: levelColor(e.levelname), py: 0.1 }}>
            <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
              {e.ts ? (formatTs ? formatTs(e.ts) : e.ts) : ''}
            </Box>
            {e.levelname && (
              <Box component="span" sx={{ mr: 1, fontWeight: 600 }}>
                {query ? <HighlightedText text={e.levelname} query={query} /> : e.levelname}
              </Box>
            )}
            {query ? (
              <HighlightedText text={String(e.msg ?? '')} query={query} />
            ) : (
              <LinkifiedText text={String(e.msg ?? '')} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
